import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BookOpen, Tag, Search, ArrowRight } from 'lucide-react';
import { SEO } from '../components/common';
import BlogCard from '../components/ui/BlogCard';
import Button from '../components/ui/Button';
import {
  getAllPosts,
  getFeaturedPosts,
  getPostsByTag,
  getAllTags,
  getRecentPosts,
  BLOG_TAGS,
  formatBlogDate,
} from '../data/blogPosts';

/**
 * Blog listing page - Docusaurus-like blog index
 */
const BlogPage = () => {
  const { tag: activeTag } = useParams();
  const [searchQuery, setSearchQuery] = useState('');

  const allPosts = useMemo(() =>
    activeTag ? getPostsByTag(activeTag) : getAllPosts(),
    [activeTag]
  );

  const filteredPosts = useMemo(() => {
    if (!searchQuery.trim()) return allPosts;
    const query = searchQuery.toLowerCase();
    return allPosts.filter(
      (post) =>
        post.title.toLowerCase().includes(query) ||
        post.description.toLowerCase().includes(query) ||
        post.tags.some((tag) => tag.toLowerCase().includes(query))
    );
  }, [allPosts, searchQuery]);

  const featuredPosts = getFeaturedPosts();
  const allTags = getAllTags();
  const recentPosts = getRecentPosts(5);

  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Blog', url: '/blog' },
    ...(activeTag ? [{ name: BLOG_TAGS[activeTag]?.label || activeTag, url: `/blog/tags/${activeTag}` }] : []),
  ];

  return (
    <>
      <SEO
        title={activeTag ? `${BLOG_TAGS[activeTag]?.label || activeTag} Articles` : 'Health Blog - MRI & Diagnostic Insights'}
        description="Read expert articles about MRI scans, diagnostic imaging, health tips, and medical insights from Mian Tufail Diagnostic Center Kasur. Stay informed about your health."
        keywords="MRI blog, health articles, diagnostic imaging tips, MRI preparation, brain MRI guide, spine MRI, medical blog Pakistan, health awareness"
        url={activeTag ? `/blog/tags/${activeTag}` : '/blog'}
        breadcrumbs={breadcrumbs}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-primary-50 to-white py-16 md:py-24">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-2xl mb-6">
              <BookOpen className="w-8 h-8 text-primary-600" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-6">
              {activeTag ? (
                <>Articles tagged: <span className="gradient-text">{BLOG_TAGS[activeTag]?.label || activeTag}</span></>
              ) : (
                <>Health <span className="gradient-text">Blog</span></>
              )}
            </h1>
            <p className="text-lg text-neutral-600 leading-relaxed">
              Expert articles and insights about MRI scans, diagnostic imaging, and health tips 
              from Mian Tufail Diagnostic Center.
            </p>
          </div>
        </div>
      </section>

      <div className="py-12 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-4 gap-10">
            {/* Main Content */}
            <div className="lg:col-span-3">
              {/* Search */}
              <div className="relative mb-8">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-neutral-700"
                />
              </div>

              {/* Featured Post (only on main blog page without search) */}
              {!activeTag && !searchQuery && featuredPosts.length > 0 && (
                <div className="mb-10">
                  <BlogCard post={featuredPosts[0]} featured />
                </div>
              )}

              {/* Posts Grid */}
              {filteredPosts.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-8">
                  {filteredPosts
                    .filter((p) => !(!activeTag && !searchQuery && p.id === featuredPosts[0]?.id))
                    .map((post) => (
                      <BlogCard key={post.id} post={post} />
                    ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <p className="text-neutral-500 text-lg mb-4">
                    No articles found{searchQuery ? ` for "${searchQuery}"` : ''}.
                  </p>
                  {searchQuery && (
                    <Button variant="outline" size="sm" onClick={() => setSearchQuery('')}>
                      Clear Search
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-1">
              {/* Tags */}
              <div className="bg-neutral-50 rounded-2xl p-6 mb-6">
                <h3 className="font-bold text-neutral-900 mb-4 flex items-center gap-2">
                  <Tag className="w-4 h-4 text-primary-600" />
                  Topics
                </h3>
                <div className="flex flex-wrap gap-2">
                  <Link
                    to="/blog"
                    className={`text-xs font-medium px-3 py-1.5 rounded-full transition-colors ${
                      !activeTag
                        ? 'bg-primary-600 text-white'
                        : 'bg-white text-neutral-600 hover:bg-primary-50 border border-neutral-200'
                    }`}
                  >
                    All
                  </Link>
                  {allTags.map((tag) => {
                    const tagInfo = BLOG_TAGS[tag];
                    if (!tagInfo) return null;
                    return (
                      <Link
                        key={tag}
                        to={`/blog/tags/${tag}`}
                        className={`text-xs font-medium px-3 py-1.5 rounded-full transition-colors ${
                          activeTag === tag
                            ? 'bg-primary-600 text-white'
                            : `${tagInfo.color} hover:opacity-80`
                        }`}
                      >
                        {tagInfo.label}
                      </Link>
                    );
                  })}
                </div>
              </div>

              {/* Recent Posts */}
              <div className="bg-neutral-50 rounded-2xl p-6 mb-6">
                <h3 className="font-bold text-neutral-900 mb-4">Recent Articles</h3>
                <div className="space-y-4">
                  {recentPosts.map((post) => (
                    <Link
                      key={post.id}
                      to={`/blog/${post.slug}`}
                      className="block group"
                    >
                      <p className="text-sm font-medium text-neutral-700 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {post.title}
                      </p>
                      <time className="text-xs text-neutral-400 mt-1 block" dateTime={post.date}>
                        {formatBlogDate(post.date)}
                      </time>
                    </Link>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="bg-primary-600 rounded-2xl p-6 text-white">
                <h3 className="font-bold text-lg mb-2">Need an MRI Scan?</h3>
                <p className="text-primary-100 text-sm mb-4">
                  Book your appointment at Mian Tufail Diagnostic Center today.
                </p>
                <Link to="/book-appointment">
                  <Button variant="accent" size="sm" icon={ArrowRight} iconPosition="right" fullWidth>
                    Book Appointment
                  </Button>
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogPage;
